package info.rezident.app.ui.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import info.rezident.app.pages.AuthUiState
import info.rezident.app.pages.AuthViewModel
import info.rezident.app.ui.theme.AppTheme
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Input

@Composable
fun AccountLinkingDialog(
    onDismiss: () -> Unit,
    onSuccess: () -> Unit
) {
    // Create ViewModel instance with success callback
    val viewModel = remember { AuthViewModel(onSuccessCallback = onSuccess) }
    val authUiState by viewModel.authUiState.collectAsState()

    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var isSignIn by remember { mutableStateOf(false) }

    // Handle successful authentication
    LaunchedEffect(authUiState) {
        if (authUiState is AuthUiState.Success) {
            onSuccess()
        }
    }

    // Reset state on mode switch
    LaunchedEffect(isSignIn) {
        viewModel.resetState()
    }

    // Overlay
    Box(
        modifier = Modifier
            .fillMaxSize()
            .backgroundColor(AppTheme.colors.header)
            .position(Position.Fixed)
            .top(0.px)
            .left(0.px)
            .zIndex(1000)
            .onClick { event ->
                // Only dismiss if clicking on the overlay itself, not its children
                if (event.target == event.currentTarget) {
                    onDismiss()
                }
            },
        contentAlignment = Alignment.Center
    ) {
        // Dialog
        Box(
            modifier = Modifier
                .width(400.px)
                .maxWidth(90.percent)
                .backgroundColor(AppTheme.colors.surface)
                .borderRadius(12.px)
                .padding(24.px)
                .boxShadow(0.px, 4.px, 20.px, 0.px, AppTheme.colors.textTertiary)
                .onClick { event ->
                    event.stopPropagation()
                    event.preventDefault()
                } // Stop propagation
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SpanText(
                    text = "Salvează-ți recomandările",
                    modifier = AppTheme.typography.h2.toModifier()
                        .color(AppTheme.colors.text)
                        .margin(bottom = 16.px)
                        .textAlign(TextAlign.Center)
                )

                SpanText(
                    text = if (isSignIn)
                        "Conectează-te pentru a-ți salva recomandările în contul tău."
                    else
                        "Creează un cont nou pentru a-ți salva recomandările.",
                    modifier = AppTheme.typography.body1.toModifier()
                        .color(AppTheme.colors.textSecondary)
                        .margin(bottom = 24.px)
                        .textAlign(TextAlign.Center)
                        .lineHeight(1.5)
                )

                val textColor = AppTheme.colors.text
                val borderColor = AppTheme.colors.textTertiary
                val backgroundColor = AppTheme.colors.surface
                // Email input
                Input(
                    type = InputType.Email,
                    attrs = {
                        style {
                            width(100.percent)
                            padding(12.px)
                            border(1.px, LineStyle.Solid, borderColor)
                            borderRadius(6.px)
                            boxSizing("border-box")
                            fontSize(16.px)
                            backgroundColor(backgroundColor)
                            color(textColor)
                        }
                        value(email)
                        onInput { event ->
                            email = event.value
                        }
                        placeholder("Email")
                    }
                )

                Box(modifier = Modifier.height(12.px))

                // Password input
                Input(
                    type = InputType.Password,
                    attrs = {
                        style {
                            width(100.percent)
                            padding(12.px)
                            border(1.px, LineStyle.Solid, borderColor)
                            borderRadius(6.px)
                            boxSizing("border-box")
                            fontSize(16.px)
                            backgroundColor(backgroundColor)
                            color(textColor)
                        }
                        value(password)
                        onInput { event ->
                            password = event.value
                        }
                        placeholder("Parolă")
                    }
                )

                // Show errors from ViewModel
                (authUiState as? AuthUiState.Error)?.let { authUiStateError ->
                    SpanText(
                        text = authUiStateError.message,
                        modifier = AppTheme.typography.body2.toModifier()
                            .fillMaxWidth()
                            .color(AppTheme.colors.error)
                            .margin(top = 4.px, bottom = 8.px)
                            .textAlign(TextAlign.Start)
                    )
                } ?: run {
                    Box(modifier = Modifier.height(12.px))
                }

                // Submit button
                Button(
                    onClick = {
                        if (email.isBlank() || password.isBlank()) {
                            return@Button
                        }

                        if (isSignIn) {
                            viewModel.signInWithEmail(email, password)
                        } else {
                            viewModel.registerWithEmail(email, password)
                        }
                    },
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(1.px, LineStyle.Solid, AppTheme.colors.text)
                        .backgroundColor(Color.transparent)
                        .color(AppTheme.colors.text)
                        .borderRadius(AppTheme.shapes.mediumRound),
                    enabled = authUiState != AuthUiState.Loading
                ) {
                    SpanText(
                        text = when (authUiState) {
                            is AuthUiState.Loading -> "Se procesează..."
                            else -> if (isSignIn) "Conectează-te" else "Creează cont"
                        },
                        modifier = AppTheme.typography.body1Medium.toModifier()
                            .color(AppTheme.colors.text)
                    )
                }

                // OR Divider
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .margin(topBottom = 16.px)
                        .gap(16.px)
                        .alignItems(AlignItems.Center),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    HorizontalDivider(
                        modifier = Modifier.weight(1f)
                    )

                    SpanText(
                        text = "SAU",
                        AppTheme.typography.body1.toModifier()
                            .fontSize(12.px)
                            .color(AppTheme.colors.textTertiary)
                    )

                    HorizontalDivider(
                        modifier = Modifier.weight(1f)
                    )
                }

                // Google Sign-In Button
                GoogleSignInButton(
                    modifier = Modifier.fillMaxWidth(),
                    isLoading = authUiState == AuthUiState.Loading,
                    enabled = authUiState != AuthUiState.Loading
                )

                // Toggle mode
                Box(modifier = Modifier.height(16.px))

                SpanText(
                    text = if (isSignIn) "Nu ai cont? Creează unul nou" else "Ai deja cont? Conectează-te",
                    modifier = AppTheme.typography.body1.toModifier()
                        .color(AppTheme.colors.textSecondary)
                        .cursor(Cursor.Pointer)
                        .textDecorationLine(TextDecorationLine.Underline)
                        .onClick { event ->
                            event.stopPropagation()
                            event.preventDefault()
                            isSignIn = !isSignIn
                            viewModel.resetState()
                        }
                )

                // Close button
                Box(modifier = Modifier.height(16.px))

                SpanText(
                    text = "Închide",
                    modifier = AppTheme.typography.body1.toModifier()
                        .color(AppTheme.colors.textSecondary)
                        .cursor(Cursor.Pointer)
                        .onClick { event ->
                            event.stopPropagation()
                            event.preventDefault()
                            onDismiss()
                        }
                )
            }
        }
    }
}